<script setup lang="ts">
//#region imports
import { get } from 'lodash-es';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import eventBus from '@/setup/eventBus';
import { typeGuard } from '@/types/core';
import type { EmitsToListeners, FormErrors } from '@/types/core';
import type { CrmAction } from '@/types/crm';
//#endregion

//#region types
interface ConfirmActionsPayload {
  defaultName: string;
  actions: CrmAction[];
}
interface ValidationErrorResponse {
  data?: {
    code?: string;
    errors?: FormErrors;
  };
}

interface Props {
  actions?: ConfirmActionsPayload | null;
  selectedActions?: boolean[] | null;
}

interface Emits {
  close: [];
}

export interface ConfirmSendTestSmsParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  actions: null,
  selectedActions: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const api = useApi();
const store = useStore();
const { t } = useI18n();
//#endregion

//#region composables
const isVisible = ref(false);
const confirmSendTestSelectedActions = ref<boolean[]>([]);
const phoneNumber = ref('+');
const confirmSendTestActions = ref<ConfirmActionsPayload | null>(null);
const errors = ref<FormErrors>({});
const isLoading = ref(false);
//#endregion

//#region computed
const phoneNumberError = computed(() => get(errors.value, ['phone_number', 0, 'message'], ''));

const languageIndex = computed<Record<string, { name?: string }>>(() => {
  const languages = store.state.misc?.languages || [];
  return languages.reduce((acc: Record<string, { name?: string }>, item: { code: string; name?: string }) => {
    acc[item.code] = item;
    return acc;
  }, {});
});

const isConfirmSendTestActionsDisabled = computed(() => (
  confirmSendTestActions.value?.actions.map((action) => !(action.message || '').trim()) || []
));

const isConfirmSendTestSelectedAll = computed<boolean>({
  get: () => {
    if (!confirmSendTestActions.value) {
      return false;
    }

    const filtered = confirmSendTestSelectedActions.value
      .filter((item, index) => !isConfirmSendTestActionsDisabled.value[index]);

    return filtered.length > 0 && filtered.every((item) => item);
  },
  set: (value) => {
    if (!confirmSendTestActions.value) {
      return;
    }

    confirmSendTestSelectedActions.value = confirmSendTestActions.value.actions
      .map((_, index) => (isConfirmSendTestActionsDisabled.value[index] ? false : value));
  },
});

const isConfirmSendTestSelectedAllDisabled = computed(() => (
  isConfirmSendTestActionsDisabled.value.every((item) => Boolean(item))
));

const isConfirmSendTestDisabled = computed(() => (
  confirmSendTestSelectedActions.value.every((item) => !item)
));
//#endregion

//#region methods
function open() {
  confirmSendTestActions.value = props.actions;
  confirmSendTestSelectedActions.value = [...(props.selectedActions || [])];
  phoneNumber.value = '+';
  errors.value = {};
  isLoading.value = false;
  isVisible.value = true;
}

function close() {
  isVisible.value = false;
  emit('close');
}

async function handleConfirmSendTest() {
  if (isLoading.value || !confirmSendTestActions.value) {
    return;
  }

  isLoading.value = true;

  const selectedActions = confirmSendTestActions.value.actions
    .filter((_, index) => confirmSendTestSelectedActions.value[index]);

  const payload = {
    actions: selectedActions.map((item) => ({
      name: item.name,
      message: item.message,
    })),
    phone_number: phoneNumber.value,
  };

  try {
    await api.sendActivityTestSms(payload);
    close();
    eventBus.$emit('notify-event', {
      type: 'info',
      text: t('crm.activities.actions.send_test_sms.success'),
    });
  } catch (error: unknown) {
    if (typeGuard<ValidationErrorResponse>(error, (typedError) => typedError?.data?.code === 'VALIDATION_ERROR')) {
      errors.value = error.data?.errors || {};
    }
  } finally {
    isLoading.value = false;
  }
}
//#endregion

//#region hooks
defineExpose({
  open,
  close,
});
//#endregion
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="$t('crm.activities.actions.send_test_sms.title')"
    :width="480"
    :is-loading="isLoading"
    @close="close()"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div class="t:flex t:gap-12">
          <UiIcon
            name="exclamation-triangle"
            :size="24"
            color="var(--text-positive)"
          />
          <UiLabel
            v-if="confirmSendTestActions"
            :label="$t('crm.activities.actions.send_test_sms.msg', {smsName: confirmSendTestActions.defaultName
              ? confirmSendTestActions.defaultName
              : $t('crm.activities.actions.empty_name'),
            })"
          />
        </div>
        <UiInput
          v-model="phoneNumber"
          input-mask="+###############"
          placeholder="+"
          :label="$t('crm.activities.actions.send_test_sms.phone_number')"
          size="big"
          autosize
          :error="phoneNumberError"
        />
        <div
          v-if="confirmSendTestActions"
          class="t:space-y-8"
        >
          <UiCheckbox
            v-model="isConfirmSendTestSelectedAll"
            :label="$t('crm.activities.actions.send_test_sms.all_localizations')"
            :disabled="isConfirmSendTestSelectedAllDisabled"
          />
          <div
            v-for="(actionGroup, index) in confirmSendTestActions.actions"
            :key="index"
            class="t:flex t:items-center t:gap-8"
          >
            <UiCheckbox
              v-model="confirmSendTestSelectedActions[index]"
              :label="languageIndex[actionGroup.language_code]?.name || actionGroup.language_code"
              :disabled="isConfirmSendTestActionsDisabled[index]"
            />
            <ActionIcon
              v-if="isConfirmSendTestActionsDisabled[index]"
              icon="exclamation-triangle"
              color="var(--text-negative)"
              :is-overlay="false"
              :tooltip="$t('crm.activities.actions.send_test_sms.empty_sms_message')"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          :disabled="isLoading"
          @click="close()"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          icon="check"
          :disabled="isConfirmSendTestDisabled || isLoading"
          filled
          @click="handleConfirmSendTest"
        >
          {{ $t('crm.buttons.confirm') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
